import { Check } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Animated } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { ASK_PROGRESS_STAGES, askProgressLabels } from './askProgressCopy';

import { TypingIndicator } from '@/components/chat/message-list/TypingIndicator';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { TWENTY_SECONDS } from '@/constants/time';
import { useCrossfade } from '@/hooks/useCrossfade';
import type { AskTurnStage } from '@/services/ask/api';

const STARTING_NOTE = 'Starting…';

const WAIT_NOTE = 'This usually takes 10–20 seconds.';

const OVERDUE_NOTE = 'Still working…';

/** What a screen reader hears as the turn moves, one stage at a time. */
function announcement(stage: AskTurnStage | null, labels: Record<AskTurnStage, string>): string {
  return stage ? `${labels[stage]}.` : 'Starting.';
}

/** How long is left of the promised wait for a turn that started at `startedMs`, a timestamp or NaN when unknown. */
function remainingWait(startedMs: number): number {
  if (Number.isNaN(startedMs)) {
    return TWENTY_SECONDS;
  }

  return Math.min(TWENTY_SECONDS, TWENTY_SECONDS - (Date.now() - startedMs));
}

/**
 * The one line under the steps that says how the wait is going: "Starting…"
 * until the turn has a stage, then the promised wait, then "Still working…"
 * once the search runs past the time it promised. Each change crossfades in
 * place, at one size, so the line reads as one message changing rather than
 * three lines taking turns.
 *
 * Where the wait is counted from depends on what this screen saw. A turn that
 * was already running when the line mounted is timed from the turn's own start,
 * so reopening a search running elsewhere does not promise the full wait again.
 * A turn watched leaving the queue is timed from its first stage instead, since
 * the turn's start includes time spent queued that the search did not use, and
 * counting it would skip the promise for a search that has only just begun. A
 * clock ahead of the server's is capped at the full wait. The clock does not run
 * while starting.
 *
 * The steps group already announces the starting state, so this line stays
 * silent until it has something else to say.
 */
function WaitNote({ startedAt, isStarting }: { startedAt?: string; isStarting: boolean }) {
  const watchedQueue = useRef(isStarting);

  const firstStageAt = useRef<number | null>(null);

  const waitStart = () => {
    if (!watchedQueue.current) {
      return startedAt ? Date.parse(startedAt) : Number.NaN;
    }

    firstStageAt.current ??= Date.now();

    return firstStageAt.current;
  };

  const [isOverdue, setIsOverdue] = useState(() => !isStarting && remainingWait(waitStart()) <= 0);

  useEffect(() => {
    if (isStarting) {
      setIsOverdue(false);

      return;
    }

    const remaining = remainingWait(waitStart());

    if (remaining <= 0) {
      setIsOverdue(true);

      return;
    }

    setIsOverdue(false);

    const timer = setTimeout(() => setIsOverdue(true), remaining);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- waitStart reads refs; only these two may restart the clock
  }, [startedAt, isStarting]);

  const runningNote = isOverdue ? OVERDUE_NOTE : WAIT_NOTE;

  const message = isStarting ? STARTING_NOTE : runningNote;

  const { opacity, displayed } = useCrossfade(message, message);

  return (
    <Animated.View style={{ opacity }}>
      <Text
        className="text-xs text-typography-700"
        accessibilityLiveRegion={isStarting ? 'none' : 'polite'}>
        {displayed}
      </Text>
    </Animated.View>
  );
}

/**
 * Where a running turn is, as three steps, worded per turn by
 * `askProgressLabels`. The group is one accessible element
 * whose label is the current stage, in a polite live region, so a screen
 * reader hears each stage change once rather than the whole list again.
 *
 * Done steps carry a check as well as
 * their tone, the current one is bold, so the state does not rest on color.
 * A turn that is queued but not yet planning shows every step pending and says
 * it is starting, which is what a worker that has not picked it up looks like.
 * That message lives in the wait note's own slot and fades into the promised
 * wait, so the two never share the screen.
 */
export function AskTurnProgress({
  stage,
  startedAt,
  turnId,
  text,
  isFollowUp,
}: {
  stage: AskTurnStage | null;
  startedAt?: string;
  turnId: string;
  text: string;
  isFollowUp: boolean;
}) {
  const labels = askProgressLabels({ seed: turnId, text, isFollowUp });

  const currentIndex = stage ? ASK_PROGRESS_STAGES.indexOf(stage) : -1;

  return (
    <VStack className="gap-3">
      <VStack
        className="gap-2"
        accessible
        accessibilityLabel={announcement(stage, labels)}
        accessibilityLiveRegion="polite">
        {ASK_PROGRESS_STAGES.map((step, index) => {
          const isDone = currentIndex > index;

          const isCurrent = currentIndex === index;

          return (
            <HStack key={step} className="items-center gap-2">
              <View
                className={twJoin(
                  'h-5 w-5 items-center justify-center rounded-full border',
                  isDone ? 'border-primary-500 bg-primary-500' : 'border-outline-500',
                  isCurrent && 'border-2 border-primary-500'
                )}>
                {isDone ? <Icon as={Check} className="h-3 w-3 text-typography-0" /> : null}
              </View>

              <Text
                className={twJoin(
                  'text-sm',
                  isCurrent ? 'font-semibold text-typography-900' : 'text-typography-700'
                )}>
                {labels[step]}
              </Text>

              {isCurrent ? <TypingIndicator decorative className="py-0" /> : null}
            </HStack>
          );
        })}
      </VStack>

      <WaitNote startedAt={startedAt} isStarting={currentIndex < 0} />
    </VStack>
  );
}
